package fr.frux.shared.di

import dev.zacsweers.metro.DependencyGraph
import fr.frux.core.ads.AdsController
import fr.frux.core.billing.StorePurchases
import fr.frux.core.common.di.AppScope
import fr.frux.core.common.io.FileExporter
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.push.PushRegistrar
import fr.frux.core.database.FruxDatabase
import fr.frux.core.maps.LocationProvider
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.notification.NotificationBadgeStore
import fr.frux.shared.domain.onboarding.OnboardingStore
import fr.frux.shared.domain.photos.TreePhotoUploads
import fr.frux.shared.domain.repository.AuthRepository
import fr.frux.shared.domain.repository.ClusterPyramidRepository
import fr.frux.shared.domain.repository.FeedbackRepository
import fr.frux.shared.domain.repository.HarvestRepository
import fr.frux.shared.domain.repository.NotificationRepository
import fr.frux.shared.domain.repository.PlaceRepository
import fr.frux.shared.domain.repository.PremiumRepository
import fr.frux.shared.domain.repository.SiteRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.SupportRepository
import fr.frux.shared.domain.repository.TreeCorrectionRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.domain.repository.UserRepository
import fr.frux.shared.domain.session.SessionStore
import io.ktor.client.HttpClient

/**
 * Main application dependency graph.
 * Metro will generate an implementation of this interface.
 *
 * Interface du graphe DI, découpage non souhaité à cette taille — cf. #108/#111.
 */
@Suppress("TooManyFunctions")
@DependencyGraph(AppScope::class)
interface AppComponent {
    /**
     * Get TreeRepository instance.
     */
    fun getTreeRepository(): TreeRepository

    /** Corrections d'identité et historique d'un arbre. */
    fun getTreeCorrectionRepository(): TreeCorrectionRepository

    /**
     * Get HarvestRepository instance.
     */
    fun getHarvestRepository(): HarvestRepository

    /**
     * Get the species reference data repository.
     */
    fun getSpeciesRepository(): SpeciesRepository

    /** Pyramide de regroupements de la carte (#622, ADR 0039). */
    fun getClusterPyramidRepository(): ClusterPyramidRepository

    /** Lieux de cueillette (ADR 0035). */
    fun getSiteRepository(): SiteRepository

    /** Recherche de lieux par géocodage (#394). */
    fun getPlaceRepository(): PlaceRepository

    /**
     * Get AuthRepository instance.
     */
    fun getAuthRepository(): AuthRepository

    /** Profil public d'un contributeur (#227). */
    fun getUserRepository(): UserRepository

    /**
     * Get the observable session store (single source of truth for auth state).
     */
    fun getSessionStore(): SessionStore

    /**
     * Get the persisted onboarding state.
     */
    fun getOnboardingStore(): OnboardingStore

    /**
     * Get the platform location provider.
     */
    fun getLocationProvider(): LocationProvider

    /**
     * Préférences applicatives. Exposées ici et non via un module de feature : `MapViewStore` vit
     * dans `feature-map`, que `shared` ne peut pas voir (règle de dépendance : les features
     * dépendent de shared, jamais l'inverse). C'est le composition root qui les assemble.
     */
    fun getAppPreferences(): AppPreferences

    /** Sauvegarde/partage d'un fichier généré côté client (#194). */
    fun getFileExporter(): FileExporter

    /** Enregistrement de l'appareil auprès du service de push (#252 P2). Inerte hors Android. */
    fun getPushRegistrar(): PushRegistrar

    /** Source unique de l'état favori, partagée par toutes les surfaces (carte, listes, détail). */
    fun getFavoritesStore(): FavoritesStore

    /** Repository du socle de notifications (#225 P0). */
    fun getNotificationRepository(): NotificationRepository

    /**
     * Compteur de notifications non lues, VM permanent au même titre que [FavoritesStore] : le
     * badge de la barre du bas ne peut pas vivre dans un `remember` d'écran (piège Nav3, cf.
     * `App.kt`) — il est rafraîchi par App au démarrage et par l'écran Activité après lecture.
     */
    fun getNotificationBadgeStore(): NotificationBadgeStore

    /**
     * Client Ktor unique de l'app (auth, timeouts, retry — cf. [fr.frux.core.network.createFruxHttpClient]).
     * Exposé pour que Coil (setSingletonImageLoaderFactory, App.kt) charge les photos au travers du
     * même moteur HTTP déjà configuré et connecté, plutôt que d'en ouvrir un second à l'aveugle (#223).
     */
    fun getHttpClient(): HttpClient

    /**
     * Base locale SQLDelight (cache offline, epic #8). Accesseur temporaire le temps que les
     * repositories la consomment (#60) : il matérialise le binding singleton sur toutes les
     * cibles. Non câblée sur js (driver absent, cf. DatabaseDriverFactory.js.kt) — ne pas
     * l'appeler depuis un chemin commun exécuté sur web tant que ce trou n'est pas comblé.
     */
    fun getFruxDatabase(): FruxDatabase

    /** Cagnotte de dons côté client (spec 2026-08-13). */
    fun getSupportRepository(): SupportRepository

    /** Retour utilisateur in-app (#437). */
    fun getFeedbackRepository(): FeedbackRepository

    /** Statut Frux+ du compte courant (#456). */
    fun getPremiumRepository(): PremiumRepository

    /** Politique publicitaire (#527, ADR 0030). Inerte hors Android et quand la configuration la coupe. */
    fun getAdsController(): AdsController

    /** Achat de Frux+ en store (#457, ADR 0033). Indisponible hors Android et en build de dev. */
    fun getStorePurchases(): StorePurchases

    /** File d'envoi des photos d'arbres, qui survit à l'assistant de création (lot 3). */
    fun getTreePhotoUploads(): TreePhotoUploads
}
